import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Scale } from "lucide-react";
import { format } from "date-fns";
import { useProofStandards, useStandardProofUploads } from "@/hooks/useProofStandards";
import type { ProofStandardType } from "@/hooks/useProofStandards";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import { cn } from "@/lib/utils";
import { PhotoMomentBadge } from "./PhotoMomentBadge";

interface ProofEvidenceSectionProps {
  submissionId: string | undefined;
  checklistId: string | undefined;
  submittedAt: string | null;
}

type CompareState = {
  title: string;
  referenceUrls: string[];
  referenceYoutubeUrl: string | null;
  proofUrls: string[];
  createdAt: string;
  standardType: ProofStandardType;
};

function MediaPreview({
  url,
  standardProofType,
  variant = "dialog",
}: {
  url: string;
  standardProofType?: ProofStandardType;
  variant?: "dialog" | "strip";
}) {
  const video = isVideoMediaUrl(url, { standardProofType });
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border bg-muted/30",
        variant === "strip" && "w-[7.5rem] shrink-0 sm:w-32"
      )}
    >
      {video ? (
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          className={cn(
            "w-full bg-black object-contain",
            variant === "strip" ? "aspect-video max-h-32" : "max-h-48"
          )}
        />
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={url}
            alt=""
            className={cn(
              "w-full object-contain",
              variant === "strip" ? "h-24 object-cover sm:h-[7.5rem]" : "h-auto max-h-48"
            )}
          />
        </a>
      )}
    </div>
  );
}

export function ProofEvidenceSection({
  submissionId,
  checklistId,
  submittedAt: _submittedAt,
}: ProofEvidenceSectionProps) {
  const { data: standards = [] } = useProofStandards(checklistId ?? undefined);
  const { data: uploads = [] } = useStandardProofUploads(submissionId);
  const [compareStandard, setCompareStandard] = useState<CompareState | null>(null);

  const uploadByStandardId = new Map(uploads.map((u) => [u.standard_id, u]));

  if (standards.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No unit checks configured on this checklist.</p>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Scale className="h-4 w-4" />
          Proof evidence
        </div>
        <p className="text-xs text-muted-foreground">
          Reference vs cleaner proof for this turnover. Use as evidence for guest claims.
        </p>
        <ul className="space-y-3">
          {standards.map((s) => {
            const upload = uploadByStandardId.get(s.id);
            const refUrls = (s.reference_media_urls ?? []) as string[];
            const proofUrls = (upload?.proof_media_urls ?? []) as string[];
            const hasProof = proofUrls.length > 0;
            const standardType = s.type as ProofStandardType;
            return (
              <li key={s.id} className="flex flex-col gap-3 rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{s.title}</span>
                    {s.room && (
                      <span className="ml-1 text-muted-foreground">({s.room})</span>
                    )}
                    {upload?.created_at && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {format(new Date(upload.created_at), "MMM d, h:mm a")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                    {refUrls.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Ref: {refUrls.length} file(s)
                      </span>
                    )}
                    {s.reference_youtube_url?.trim() ? (
                      <span className="text-xs text-muted-foreground">YouTube demo</span>
                    ) : null}
                    {hasProof ? (
                      <span className="text-xs text-green-600">Proof: {proofUrls.length} file(s)</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No proof</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCompareStandard({
                          title: s.title,
                          referenceUrls: refUrls,
                          referenceYoutubeUrl: s.reference_youtube_url?.trim() ?? null,
                          proofUrls,
                          createdAt: upload?.created_at ?? "",
                          standardType,
                        })
                      }
                    >
                      Compare
                    </Button>
                  </div>
                </div>

                {(refUrls.length > 0 || s.reference_youtube_url?.trim() || proofUrls.length > 0) && (
                  <div className="flex flex-col gap-3 border-t border-border/60 pt-3 sm:flex-row sm:items-start sm:gap-4">
                    {refUrls.length > 0 || s.reference_youtube_url?.trim() ? (
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Reference
                        </p>
                        {s.reference_youtube_url?.trim() ? (
                          <p className="text-sm">
                            <a
                              href={s.reference_youtube_url.trim()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline"
                            >
                              Open host YouTube demo
                            </a>
                          </p>
                        ) : null}
                        {refUrls.length > 0 ? (
                          <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
                            {refUrls.map((url, i) => (
                              <MediaPreview key={`ref-${s.id}-${i}`} url={url} variant="strip" />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {proofUrls.length > 0 ? (
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Cleaner proof
                          </p>
                          <PhotoMomentBadge url={proofUrls[0]} fallback="cleaning_proof" short />
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
                          {proofUrls.map((url, i) => (
                            <MediaPreview
                              key={`proof-${s.id}-${i}`}
                              url={url}
                              standardProofType={standardType}
                              variant="strip"
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 rounded-md border border-dashed bg-muted/20 px-2 py-4 text-center text-xs text-muted-foreground">
                        No proof uploaded yet
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <Dialog open={!!compareStandard} onOpenChange={() => setCompareStandard(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{compareStandard?.title} — Reference vs Proof</DialogTitle>
            {compareStandard?.createdAt && (
              <p className="text-sm text-muted-foreground">
                Proof uploaded {format(new Date(compareStandard.createdAt), "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </DialogHeader>
          {compareStandard && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <h4 className="mb-2 text-sm font-medium">Reference</h4>
                <div className="space-y-2">
                  {compareStandard.referenceYoutubeUrl ? (
                    <p className="text-sm">
                      <a
                        href={compareStandard.referenceYoutubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline"
                      >
                        Open host YouTube demo
                      </a>
                    </p>
                  ) : null}
                  {compareStandard.referenceUrls.length > 0 ? (
                    compareStandard.referenceUrls.map((url, i) => (
                      <MediaPreview key={i} url={url} variant="dialog" />
                    ))
                  ) : !compareStandard.referenceYoutubeUrl ? (
                    <p className="text-sm text-muted-foreground">No reference media</p>
                  ) : null}
                </div>
              </div>
              <div>
                <h4 className="mb-2 text-sm font-medium">Cleaner proof</h4>
                <div className="space-y-2">
                  {compareStandard.proofUrls.length > 0 ? (
                    compareStandard.proofUrls.map((url, i) => (
                      <MediaPreview
                        key={i}
                        url={url}
                        standardProofType={compareStandard.standardType}
                        variant="dialog"
                      />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No proof uploaded</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
